/**
 * Public IT Solutions Product Detail page.
 *
 * Lenovo-inspired layout but on BMP brand:
 *  - Breadcrumb
 *  - Hero gallery (main image + thumbnails)
 *  - Right column: brand pill, name, short description, key highlights,
 *    "Add to quote" CTA, partner trust band.
 *  - Sticky section nav: Overview · Tech Specs · Ports & Slots · About BMP
 *  - Related products
 *  - Legal disclaimer
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowLeft, ShieldCheck, Truck, FileText, Sparkles, Cpu, Monitor,
  HardDrive, Battery, Camera, Layers, Plus, Check, Building2, Globe2,
  CheckCircle, Loader2, X, Send, Mail, Phone, ChevronLeft, ChevronRight,
} from 'lucide-react';
import FloatingNavWidget from '../components/public/FloatingNavWidget';


/**
 * ThumbnailCarousel — horizontally scrollable thumbnail strip with chevron
 * controls. Shows 6 thumbs per "page"; when the product has more, chevrons
 * appear and the user can scroll. PDF generator is intentionally unaffected
 * (capped to 4-6 photos by design).
 */
const ThumbnailCarousel = ({ gallery, heroIdx, setHeroIdx }) => {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const recompute = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    recompute();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => { el.removeEventListener('scroll', recompute); window.removeEventListener('resize', recompute); };
  }, [gallery]);

  // Keep the active thumb visible inside the scroller
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const node = el.querySelector(`[data-thumb-idx="${heroIdx}"]`);
    if (node) node.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [heroIdx]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <div className="relative mt-4">
      <div
        ref={scrollerRef}
        className="flex gap-2 overflow-x-auto scroll-smooth no-scrollbar"
        style={{ scrollbarWidth: 'none' }}
        data-testid="thumb-carousel"
      >
        {gallery.map((g, i) => (
          <button
            key={i}
            data-thumb-idx={i}
            onClick={() => setHeroIdx(i)}
            className={`flex-shrink-0 w-[15%] min-w-[64px] aspect-square rounded-lg border-2 overflow-hidden bg-white transition ${
              i === heroIdx ? 'border-[#FF6B35]' : 'border-slate-100 hover:border-slate-300'
            }`}
            data-testid={`thumb-${i}`}
            aria-label={`Image ${i + 1} of ${gallery.length}`}
          >
            <img src={g} alt="" className="w-full h-full object-contain" loading="lazy" />
          </button>
        ))}
      </div>
      {canPrev && (
        <button
          onClick={() => scrollBy(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-8 h-8 rounded-full bg-white/95 border border-slate-200 shadow-md flex items-center justify-center text-[#0F172A] hover:bg-white"
          aria-label="Previous thumbnails"
          data-testid="thumb-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      {canNext && (
        <button
          onClick={() => scrollBy(1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-8 h-8 rounded-full bg-white/95 border border-slate-200 shadow-md flex items-center justify-center text-[#0F172A] hover:bg-white"
          aria-label="Next thumbnails"
          data-testid="thumb-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
      <div className="text-[10px] text-[#6C757D] mt-2 text-right" data-testid="thumb-counter">
        {heroIdx + 1} / {gallery.length}
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_LANGS = ['pt', 'en', 'fr', 'ar', 'zh'];
const toCode = (language) => {
  const b = (language || 'en').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(b) ? b : 'en';
};

const SECTION_DEFINITIONS = [
  { id: 'overview' },
  { id: 'specs' },
  { id: 'ports' },
  { id: 'about' },
];

// Map common spec labels → icons for the highlight chips
const HIGHLIGHT_ICON_RULES = [
  { rx: /cpu|processor/i, icon: Cpu, key: 'CPU' },
  { rx: /memory|ram\b/i, icon: HardDrive, key: 'RAM' },
  { rx: /storage|ssd|nvme/i, icon: Layers, key: 'Storage' },
  { rx: /display|screen|resolution/i, icon: Monitor, key: 'Display' },
  { rx: /battery|power/i, icon: Battery, key: 'Battery' },
  { rx: /camera|webcam/i, icon: Camera, key: 'Camera' },
];

const SUPPLIER_LOGOS = {
  Lenovo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ce/Lenovo_Global_Corporate_Logo.png/320px-Lenovo_Global_Corporate_Logo.png',
};

const ITSolutionsDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const code = toCode(language);

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);
  const [section, setSection] = useState('overview');
  const [quoteOpen, setQuoteOpen] = useState(false);

  // -- Load product + related --
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setHeroIdx(0);
    setSection('overview');
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/public/it-catalogue/${slug}`, { params: { lang: code } });
        if (!alive) return;
        setProduct(r.data);
        // Fetch related (same brand, exclude self) — capped to 4
        const rel = await axios.get(`${API_URL}/api/public/it-catalogue/`, {
          params: { brand: r.data.brand, limit: 8, lang: code },
        });
        if (!alive) return;
        setRelated((rel.data || []).filter((p) => p.id !== r.data.id).slice(0, 4));
      } catch (e) {
        setError(e.response?.data?.detail || 'Product not found.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug, code]);

  // Gallery sources
  const gallery = useMemo(() => {
    if (!product) return [];
    const items = [];
    if (product.hero_image_url) items.push(product.hero_image_url);
    for (const g of product.gallery_urls || []) {
      if (!items.includes(g)) items.push(g);
    }
    return items;
  }, [product]);

  const heroImg = gallery[heroIdx] || product?.hero_image_url;

  // Build highlight chips by matching specs against rules — limit to 6
  const highlights = useMemo(() => {
    if (!product?.specs) return [];
    const chips = [];
    const seen = new Set();
    for (const rule of HIGHLIGHT_ICON_RULES) {
      const m = product.specs.find((s) => rule.rx.test(s.label || ''));
      if (m && !seen.has(rule.key)) {
        chips.push({ ...m, icon: rule.icon, key: rule.key });
        seen.add(rule.key);
      }
    }
    return chips.slice(0, 6);
  }, [product]);

  // -- Loading & error states --
  if (loading) {
    return (
      <div className="min-h-screen pt-40 pb-24 flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-[#023E8A] animate-spin" />
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className="min-h-screen pt-44 pb-24 bg-white">
        <div className="container mx-auto px-6 max-w-2xl text-center">
          <div className="text-6xl mb-4">🛰️</div>
          <h2 className="text-2xl font-extrabold text-[#2B2D42] mb-3">{t('itSolutions.detail.notFound')}</h2>
          <p className="text-[#6C757D] mb-6">{error || t('itSolutions.detail.notFoundMsg')}</p>
          <Link to="/it-solutions" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F172A] text-white font-bold hover:bg-[#1E293B] transition">
            <ArrowLeft className="w-4 h-4" /> {t('itSolutions.detail.backToCatalogue')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="it-product-detail">
      {/* ============ TOP BAR · Breadcrumb ============ */}
      <div className="pt-32 pb-2 bg-white border-b border-slate-100">
        <div className="container mx-auto px-6">
          <nav className="text-[12px] text-[#6C757D] flex items-center gap-1.5 flex-wrap">
            <Link to="/" className="hover:text-[#0F172A]">{t('itSolutions.detail.home')}</Link>
            <span>›</span>
            <Link to="/services" className="hover:text-[#0F172A]">{t('itSolutions.detail.services')}</Link>
            <span>›</span>
            <Link to="/it-solutions" className="hover:text-[#0F172A]">{t('itSolutions.detail.itSolutions')}</Link>
            <span>›</span>
            <span className="text-[#0F172A] font-medium">{product.brand}</span>
            <span>›</span>
            <span className="text-[#0F172A] font-medium truncate max-w-[300px]">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* ============ HERO ============ */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-10">
        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Gallery */}
          <div>
            <div className="aspect-[5/4] bg-white rounded-3xl border border-slate-100 overflow-hidden flex items-center justify-center shadow-sm">
              {heroImg ? (
                <img src={heroImg} alt={product.name} className="max-w-full max-h-full object-contain" data-testid="product-hero-image" />
              ) : (
                <div className="text-slate-300 text-6xl">📦</div>
              )}
            </div>
            {gallery.length > 1 && (
              <ThumbnailCarousel gallery={gallery} heroIdx={heroIdx} setHeroIdx={setHeroIdx} />
            )}
            {/* Trust band */}
            <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-4 grid grid-cols-3 gap-3 text-center">
              <div className="flex flex-col items-center gap-1 text-[11px] text-[#6C757D]">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                {t('itSolutions.detail.trustPartner')}
              </div>
              <div className="flex flex-col items-center gap-1 text-[11px] text-[#6C757D]">
                <Truck className="w-5 h-5 text-[#00B4D8]" />
                {t('itSolutions.detail.trustDelivery')}
              </div>
              <div className="flex flex-col items-center gap-1 text-[11px] text-[#6C757D]">
                <FileText className="w-5 h-5 text-[#FF6B35]" />
                {t('itSolutions.detail.trustPricing')}
              </div>
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={() => navigate(`/it-solutions?brand=${encodeURIComponent(product.brand)}`)}
                className="inline-block px-3 py-1 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-[11px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] hover:text-white transition"
                data-testid="detail-brand-pill"
                title={`See more from ${product.brand}`}
              >
                {product.brand}
              </button>
              {product.is_featured && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-widest">
                  <Sparkles className="w-3 h-3" /> {t('itSolutions.detail.featured')}
                </span>
              )}
              <span className="text-[10px] font-mono text-[#6C757D]">{product.sku}</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0F172A] leading-[1.05] tracking-tight mb-5">
              {product.name}
            </h1>

            <p className="text-[15px] text-[#475569] leading-relaxed mb-6">
              {product.short_description}
            </p>

            {/* Highlight chips */}
            {highlights.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                {highlights.map((h) => (
                  <div key={h.key} className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <h.icon className="w-4 h-4 text-[#023E8A] flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#6C757D]">{h.key}</div>
                      <div className="text-[13px] font-semibold text-[#0F172A] truncate">{h.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CTAs */}
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold tracking-tight shadow-xl hover:scale-[1.03] transition-all"
                data-testid="detail-add-to-quote-btn"
              >
                <Plus className="w-4 h-4" /> {t('itSolutions.detail.requestQuotation')}
              </button>
              <Link
                to="/contact"
                className="text-sm font-bold text-[#0F172A] hover:underline underline-offset-4 flex items-center gap-1.5"
              >
                <Mail className="w-4 h-4" /> {t('itSolutions.detail.orTalk')}
              </Link>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-[12px] text-amber-900 leading-relaxed flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <span>
                {t('itSolutions.detail.disclaimerTrademark', { brand: product.brand })}
                <span> {t('itSolutions.detail.disclaimerPartner')}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STICKY TABS ============ */}
      <div className="sticky top-[72px] z-30 bg-white/95 backdrop-blur-md border-y border-slate-100">
        <div className="container mx-auto px-6 flex items-center gap-1 overflow-x-auto">
          {SECTION_DEFINITIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setSection(s.id);
                document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`relative py-4 px-5 text-sm font-bold tracking-tight whitespace-nowrap transition ${
                section === s.id ? 'text-[#0F172A]' : 'text-[#6C757D] hover:text-[#0F172A]'
              }`}
              data-testid={`tab-${s.id}`}
            >
              {t(`itSolutions.detail.tab_${s.id}`)}
              {section === s.id && <span className="absolute bottom-0 left-3 right-3 h-[3px] bg-[#FF6B35] rounded-t" />}
            </button>
          ))}
          <div className="flex-1" />
          <a
            href={`${API_URL}/api/public/it-catalogue/${product.slug || product.id}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center gap-2 my-2 mr-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-[#0F172A] text-xs font-bold hover:bg-slate-50 transition"
            data-testid="download-spec-pdf-btn"
          >
            <FileText className="w-3.5 h-3.5 text-[#FF6B35]" /> {t('itSolutions.detail.downloadSpec')}
          </a>
          <button
            onClick={() => setQuoteOpen(true)}
            className="hidden md:inline-flex items-center gap-2 my-2 px-4 py-2 rounded-full bg-[#0F172A] text-white text-xs font-bold hover:bg-[#1E293B] transition"
          >
            <Plus className="w-3.5 h-3.5" /> {t('itSolutions.addToQuote')}
          </button>
        </div>
      </div>

      {/* ============ OVERVIEW / MARKETING ============ */}
      <section id="section-overview" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <SectionTitle eyebrow={t('itSolutions.detail.overviewEyebrow')} title={t('itSolutions.detail.overviewTitle')} />
          {(product.overview_html || product.marketing_copy_html) ? (
            <div
              className="prose prose-slate max-w-none text-[15px] leading-relaxed text-[#334155]"
              dangerouslySetInnerHTML={{ __html: product.overview_html || product.marketing_copy_html }}
            />
          ) : (
            <p className="text-[15px] leading-relaxed text-[#475569] whitespace-pre-line">{product.short_description}</p>
          )}

          {/* Marketing image grid (uses gallery slots 1..N as feature blocks) */}
          {gallery.length > 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              {gallery.slice(1, 5).map((g, i) => (
                <div key={i} className="aspect-[16/10] rounded-2xl overflow-hidden bg-slate-50 border border-slate-100">
                  <img src={g} alt="" className="w-full h-full object-contain hover:scale-[1.03] transition-transform duration-500" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ TECH SPECS ============ */}
      <section id="section-specs" className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 max-w-5xl">
          <SectionTitle eyebrow={t('itSolutions.detail.specsEyebrow')} title={t('itSolutions.detail.specsTitle')} />
          {(product.specs || []).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-[#6C757D]">
              {t('itSolutions.detail.specsEmptyTitle')}
              <div className="mt-2 text-[13px]">
                {t('itSolutions.detail.specsEmptyMsg')}
              </div>
            </div>
          ) : (
            <GroupedSpecs specs={product.specs} />
          )}
        </div>
      </section>

      {/* ============ PORTS & SLOTS ============ */}
      <section id="section-ports" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <SectionTitle eyebrow={t('itSolutions.detail.portsEyebrow')} title={t('itSolutions.detail.portsTitle')} />
          <PortsAndSlots product={product} />
        </div>
      </section>

      {/* ============ ABOUT BMP ============ */}
      <section id="section-about" className="py-20 bg-gradient-to-br from-[#0F172A] via-[#023E8A] to-[#0F172A] text-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <SectionTitle eyebrow={t('itSolutions.detail.aboutEyebrow')} title={t('itSolutions.detail.aboutTitle')} theme="dark" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {[
              { icon: Building2, title: t('itSolutions.detail.aboutCard1Title'), d: t('itSolutions.detail.aboutCard1Desc') },
              { icon: Truck, title: t('itSolutions.detail.aboutCard2Title'), d: t('itSolutions.detail.aboutCard2Desc') },
              { icon: ShieldCheck, title: t('itSolutions.detail.aboutCard3Title'), d: t('itSolutions.detail.aboutCard3Desc') },
            ].map((b, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition">
                <b.icon className="w-6 h-6 text-amber-300 mb-3" />
                <div className="text-base font-extrabold mb-2">{b.title}</div>
                <p className="text-[13px] text-white/70 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button
              onClick={() => setQuoteOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold tracking-tight shadow-xl hover:scale-[1.03] transition-all"
            >
              <Plus className="w-4 h-4" /> {t('itSolutions.detail.requestProduct')}
            </button>
          </div>
        </div>
      </section>

      {/* ============ RELATED ============ */}
      {related.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 max-w-6xl">
            <SectionTitle eyebrow={t('itSolutions.detail.relatedEyebrow')} title={t('itSolutions.detail.relatedTitle', { brand: product.brand })} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/it-solutions/${p.slug || p.id}`}
                  className="group rounded-2xl border border-slate-100 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid={`related-${p.sku}`}
                >
                  <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center overflow-hidden">
                    {p.hero_image_url ? (
                      <img src={p.hero_image_url} alt={p.name} className="max-w-full max-h-full object-contain group-hover:scale-[1.05] transition-transform duration-500" />
                    ) : <span className="text-slate-300 text-3xl">📦</span>}
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6B35]">{p.brand}</div>
                    <div className="text-[13px] font-bold text-[#0F172A] line-clamp-2 mt-1 min-h-[36px]">{p.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ FOOTER DISCLAIMER ============ */}
      <section className="py-8 bg-white border-t border-slate-100">
        <div className="container mx-auto px-6 max-w-4xl text-center text-[11px] text-[#6C757D] leading-relaxed">
          {t('itSolutions.detail.footerDisclaimer')}
        </div>
      </section>

      {/* ============ STICKY MOBILE CTA ============ */}
      <div className="lg:hidden fixed bottom-4 left-4 right-4 z-40">
        <button
          onClick={() => setQuoteOpen(true)}
          className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold shadow-2xl flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> {t('itSolutions.addToQuote')}
        </button>
      </div>

      {/* ============ QUOTE DIALOG ============ */}
      {quoteOpen && (
        <SingleProductQuoteDialog
          product={product}
          onClose={() => setQuoteOpen(false)}
        />
      )}

      <FloatingNavWidget backTo="/it-solutions" />
    </div>
  );
};


const SectionTitle = ({ eyebrow, title, theme = 'light' }) => (
  <div className="mb-8">
    <div className={`text-[11px] font-bold uppercase tracking-[0.2em] mb-2 ${theme === 'dark' ? 'text-amber-300' : 'text-[#FF6B35]'}`}>
      {eyebrow}
    </div>
    <h2 className={`text-2xl md:text-3xl font-extrabold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-[#0F172A]'}`}>
      {title}
    </h2>
  </div>
);


// ============================================================================
// Tech Specs grouped by category (Performance · Connectivity · Design · Sustainability)
// Lenovo-style accordions. Falls back to a single flat table if no spec has a category.
// ============================================================================
const SPEC_CATEGORY_ORDER = ['Performance', 'Connectivity', 'Design', 'Sustainability', 'Other'];

const GroupedSpecs = ({ specs }) => {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const buckets = {};
    let categorised = 0;
    for (const s of specs) {
      const cat = (s.category && SPEC_CATEGORY_ORDER.includes(s.category)) ? s.category : 'Other';
      if (s.category) categorised += 1;
      (buckets[cat] = buckets[cat] || []).push(s);
    }
    return { buckets, categorised };
  }, [specs]);

  // Default: open the first group; rest collapsed.
  const orderedCats = SPEC_CATEGORY_ORDER.filter((c) => groups.buckets[c]?.length);
  const [open, setOpen] = useState(() => new Set([orderedCats[0]]));

  // Fallback: no categories at all → render the old flat table.
  if (groups.categorised === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden divide-y divide-slate-100" data-testid="tech-specs-table">
        {specs.map((s, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3 px-6 py-3.5 hover:bg-slate-50">
            <div className="text-[12px] uppercase tracking-widest font-bold text-[#6C757D]">{s.label}</div>
            <div className="text-[14px] text-[#0F172A] leading-relaxed">{s.value}</div>
          </div>
        ))}
      </div>
    );
  }

  const toggle = (cat) => {
    const next = new Set(open);
    if (next.has(cat)) next.delete(cat); else next.add(cat);
    setOpen(next);
  };

  return (
    <div className="space-y-3" data-testid="tech-specs-grouped">
      {orderedCats.map((cat) => {
        const items = groups.buckets[cat] || [];
        const isOpen = open.has(cat);
        return (
          <div key={cat} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <button
              onClick={() => toggle(cat)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition"
              data-testid={`specs-group-${cat.toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                <span className="inline-block w-1.5 h-6 rounded-sm bg-[#FF6B35]" />
                <span className="text-[13px] uppercase tracking-[0.18em] font-extrabold text-[#0F172A]">{t(`itSolutions.detail.specCat.${cat}`, { defaultValue: cat })}</span>
                <span className="text-[11px] font-mono text-[#6C757D]">{t('itSolutions.detail.specsItems', { count: items.length })}</span>
              </div>
              <span className={`text-[#6C757D] transition-transform ${isOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {isOpen && (
              <div className="divide-y divide-slate-100 border-t border-slate-100">
                {items.map((s, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3 px-6 py-3.5 hover:bg-slate-50">
                    <div className="text-[12px] uppercase tracking-widest font-bold text-[#6C757D]">{s.label}</div>
                    <div className="text-[14px] text-[#0F172A] leading-relaxed">{s.value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ============================================================================
// Ports & Slots — Lenovo-style diagram + numbered legend.
// ============================================================================
const PortsAndSlots = ({ product }) => {
  const { t } = useTranslation();
  // Decide if we have a "numbered legend" shape: ports[].label is mostly numeric.
  const ports = (product.ports || []).filter((p) => p && (p.label || p.value));
  const numericLabels = ports.filter((p) => /^\d{1,2}$/.test((p.label || '').trim())).length;
  const isNumberedLegend = ports.length > 0 && (numericLabels / ports.length) >= 0.6;
  const diagram = product.ports_image_url;

  // Premium layout: diagram + numbered legend
  if (diagram && isNumberedLegend) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start" data-testid="ports-numbered-layout">
        <div className="bg-slate-50 rounded-3xl border border-slate-100 p-6 flex items-center justify-center">
          <img
            src={diagram}
            alt={t('itSolutions.detail.portsDiagramAlt')}
            className="max-w-full max-h-[420px] object-contain"
            data-testid="ports-diagram-image"
          />
        </div>
        <div>
          <ol className="space-y-3">
            {ports.map((p, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold text-[13px] flex items-center justify-center shadow-sm">
                  {p.label}
                </span>
                <div className="pt-1 text-[14px] text-[#0F172A] leading-relaxed">{p.value}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }

  // Fallback: keyword-extracted from specs OR legacy label/value grid
  const fallback = ports.length > 0
    ? ports
    : (product.specs || []).filter((s) =>
        /usb|port|hdmi|thunder|jack|reader|wireless|ether|bluetooth|wifi|wi-fi/i.test(s.label || '')
      );

  if (fallback.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-[#6C757D]">
        {t('itSolutions.detail.portsEmpty')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fallback.map((s, i) => (
        <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
          <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-1" />
          <div>
            <div className="text-[12px] uppercase tracking-widest font-bold text-[#6C757D]">{s.label}</div>
            <div className="text-[14px] text-[#0F172A]">{s.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
};


// ============================================================================
// Single-product quote dialog (lighter than the multi-cart one on the listing)
// ============================================================================
const SingleProductQuoteDialog = ({ product, onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company_name: '', contact_name: '', contact_email: '', contact_phone: '',
    company_trn: '', country: '', notes: '', quantity: 1,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const submit = async () => {
    if (!form.company_name.trim() || !form.contact_email.trim() || !form.contact_name.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/public/it-quote-request/`, {
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        company_trn: form.company_trn,
        country: form.country,
        notes: form.notes,
        items: [{
          product_id: product.id,
          sku: product.sku,
          name: product.name,
          quantity: Number(form.quantity) || 1,
        }],
      });
      setSuccess(r.data);
    } catch (_) {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-xl font-extrabold text-[#2B2D42] mb-2">{t('itSolutions.quote.received')}</h3>
          <p className="text-sm text-[#6C757D] mb-2">
            {t('itSolutions.quote.reference')} <span className="font-mono font-bold text-[#023E8A]">{success.reference}</span>
          </p>
          <p className="text-sm text-[#6C757D] mb-6">
            {t('itSolutions.quote.receivedMsg')}
          </p>
          <button onClick={onClose} className="px-6 py-2.5 rounded-xl bg-[#0F172A] text-white font-bold hover:bg-[#1E293B] transition">
            {t('itSolutions.quote.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white sm:rounded-2xl w-full max-w-2xl max-h-[95vh] flex flex-col shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5">{t('itSolutions.quote.badge')}</div>
            <h3 className="text-lg font-extrabold">{t('itSolutions.quote.title')}</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="single-quote-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {/* Product summary */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
            {product.hero_image_url && (
              <img src={product.hero_image_url} alt="" className="w-16 h-16 object-contain rounded-md bg-white border border-slate-100" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6B35]">{product.brand}</div>
              <div className="text-sm font-bold text-[#2B2D42] truncate">{product.name}</div>
              <div className="text-[11px] font-mono text-[#6C757D]">{product.sku}</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setForm({ ...form, quantity: Math.max(1, form.quantity - 1) })} className="w-7 h-7 rounded-md bg-white border border-gray-200 font-bold">−</button>
              <span className="w-8 text-center text-sm font-bold tabular-nums">{form.quantity}</span>
              <button onClick={() => setForm({ ...form, quantity: form.quantity + 1 })} className="w-7 h-7 rounded-md bg-white border border-gray-200 font-bold">+</button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('itSolutions.quote.companyName')} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="detail-quote-company" />
            <Field label={t('itSolutions.quote.country')} value={form.country} onChange={(v) => setForm({ ...form, country: v })} placeholder="UAE" />
            <Field label={t('itSolutions.quote.contactName')} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="detail-quote-contact" />
            <Field label={t('itSolutions.quote.contactEmail')} type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="detail-quote-email" />
            <Field label={t('itSolutions.quote.phone')} value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
            <Field label={t('itSolutions.quote.trn')} value={form.company_trn} onChange={(v) => setForm({ ...form, company_trn: v })} />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">{t('itSolutions.quote.notes')}</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]"
              placeholder={t('itSolutions.quote.notesPlaceholder')}
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex items-center justify-between sm:rounded-b-2xl">
          <span className="text-xs text-[#6C757D]">{t('itSolutions.quote.respondNote')}</span>
          <button
            onClick={submit}
            disabled={submitting || !form.company_name.trim() || !form.contact_email.trim() || !form.contact_name.trim()}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold disabled:opacity-40 hover:scale-[1.02] transition"
            data-testid="detail-quote-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('itSolutions.quote.submitting')}</> : <><Send className="w-4 h-4" /> {t('itSolutions.quote.submit')}</>}
          </button>
        </div>
      </div>
    </div>
  );
};


const Field = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]"
      data-testid={testid}
    />
  </div>
);

export default ITSolutionsDetailPage;
